/**
 * The Help content's key for the page at an address (APP-048). The server knows pages by key, not
 * URL, so a route can move without the help changing; a route may also name its key in
 * `handle.help.pageKey`. Anything unknown is Home's help.
 *
 * Recognize shows an award's entry form at `/recognize?type=…` (APP-090): that is award entry, so
 * Help answers about the form, and "Show me" can point at its cards. Each Award setup page in
 * Settings has its own key (APP-094); only My preferences falls to `/settings` itself. Feedback's
 * list and its reports have theirs too (APP-104), ahead of System administration's, and so do the
 * sender's My feedback and each of their reports (APP-138), ahead of My preferences'. Manage's
 * pages (APP-234, and the Executive group's, APP-236) keep the keys they had at their earlier
 * addresses, so their Help, its "Show me" targets and Help chat's suggestions are the same
 * wherever they live.
 */
const RULES: readonly (readonly [RegExp, string])[] = [
  [/^\/$/, 'home'],
  [/^\/my-rewards\/[^/]+\/election\/?$/, 'election'],
  [/^\/my-rewards(?:\/(?:elections|documents))?\/?$/, 'my-rewards'],
  [/^\/my-rewards\/[^/]+\/?$/, 'my-award'],
  [/^\/letters-to-hand-out(?:\/|$)/, 'letters-to-hand-out'],
  [/^\/recognize\/?$/, 'recognize'],
  [/^\/recognize\/spot(?:\/|$)/, 'spot'],
  // APP-234: Manage's pages, by the keys they had before.
  [/^\/manage\/spot(?:\/|$)/, 'spot'],
  [/^\/manage\/queue\/letters\/?$/, 'work-letters'],
  [/^\/manage\/queue\/past-deadline\/?$/, 'work-past-deadline'],
  [/^\/manage\/queue(?:\/(?:mine|in-progress|tax))?\/?$/, 'work-queue'],
  [/^\/manage\/payroll(?:\/|$)/, 'payroll'],
  [/^\/manage\/earning-statements(?:\/|$)/, 'work-earning-statements'],
  [/^\/manage\/shareworks(?:\/|$)/, 'work-shareworks'],
  [/^\/recognize\//, 'award-entry'],
  [/^\/awards\/?$/, 'my-awards'],
  [/^\/awards\//, 'award-record'],
  [/^\/approvals(?:\/|$)/, 'approvals'],
  [/^\/queue\/letters\/?$/, 'work-letters'],
  [/^\/queue\/past-deadline\/?$/, 'work-past-deadline'],
  [/^\/queue\/shareworks\/?$/, 'work-shareworks'],
  [/^\/queue\/earning-statements\/?$/, 'work-earning-statements'],
  [/^\/queue(?:\/(?:mine|in-progress|tax))?\/?$/, 'work-queue'],
  [/^\/queue\/[^/]+\/?$/, 'work-check'],
  [/^\/queue\/[^/]+\/letters\/?$/, 'work-letters'],
  [/^\/payroll(?:\/|$)/, 'payroll'],
  [/^\/work\/earning-statements(?:\/|$)/, 'work-earning-statements'],
  [/^\/work\/shareworks(?:\/|$)/, 'work-shareworks'],
  [/^\/insights\/ask(?:\/|$)/, 'insights-ask'],
  [/^\/insights(?:\/|$)/, 'insights'],
  // APP-236: the Executive group's pages under Manage, by the keys they had at `/executive/…`
  // (whose old addresses redirect).
  [/^\/(?:manage\/)?executive\/budget-pools(?:\/|$)/, 'budget-pools'],
  [/^\/(?:manage\/)?executive\/values(?:\/|$)/, 'values'],
  [/^\/(?:manage\/)?executive(?:\/|$)/, 'executive'],
  [/^\/settings\/sector(?:\/|$)/, 'sector-settings'],
  [/^\/settings\/delegates(?:\/|$)/, 'executive-delegates'],
  [/^\/settings\/charge-codes(?:\/|$)/, 'charge-codes'],
  [/^\/settings\/budgets(?:\/|$)/, 'budget-pools'],
  [/^\/settings\/values(?:\/|$)/, 'values'],
  [/^\/settings\/elections-letters(?:\/|$)/, 'elections-letters'],
  [/^\/settings\/payroll(?:\/|$)/, 'payroll-settings'],
  [/^\/settings\/help-catalog(?:\/|$)/, 'help-catalog'],
  [/^\/settings\/messages(?:\/|$)/, 'messages'],
  // APP-222: Spot Award setup has its own Help, as the other Award setup pages do.
  [/^\/settings\/spot(?:\/|$)/, 'spot-setup'],
  [/^\/(?:admin|settings\/system)\/ai(?:\/|$)/, 'admin-ai'],
  [/^\/(?:admin|settings\/system)\/feedback(?:\/|$)/, 'admin-feedback'],
  [/^\/(?:admin|settings\/system)(?:\/|$)/, 'admin'],
  [/^\/settings\/me\/feedback(?:\/|$)/, 'my-feedback'],
  [/^\/settings(?:\/|$)/, 'my-settings'],
  [/^\/training(?:\/|$)/, 'training'],
];

export function helpPageKey(pathname: string, declared?: string, search = ''): string {
  if (declared !== undefined && /^[a-z][a-z0-9-]{0,40}$/.test(declared)) return declared;
  if (/^\/recognize\/?$/.test(pathname) && new URLSearchParams(search).has('type'))
    return 'award-entry';
  for (const [pattern, key] of RULES) if (pattern.test(pathname)) return key;
  return 'home';
}
